import NeptuneElement from '../../NeptuneElement.js';
class Header extends NeptuneElement {
    templateMarkup = `
        <header>
            <button id="hamburgerButton" aria-label="Toggle navigation">
                <i class="fa-solid fa-bars"></i>
            </button>
            <div>
                <a href="https://www.ecolab.com" target="_blank" aria-label="Visit Ecolab.com">
                    <img src="/src/components/chrome/assets/ecolab-logo/ecolab-logo.svg" alt="Company Logo" />
                </a>
                <span>|</span>
                <slot name="application-name"></slot>
            </div>
            <nav id="headerActionNav" data-has-notifications="false" data-has-apps="false" data-has-profile="false">
                <neptune-header-action-item id="notificationActionItem" data-type="nav-item-notifications">
                    <button slot="header-items" aria-label="Open notifications">
                        <i class="fa-regular fa-bell"></i>
                    </button>
                </neptune-header-action-item>
                <neptune-header-action-item id="appsActionItem" data-type="nav-item-apps">
                    <button slot="header-items" aria-label="Open app menu">
                        <i class="fa-solid fa-grid"></i>
                    </button>
                </neptune-header-action-item>
                <neptune-header-action-item id="profileActionItem" data-type="nav-item-profile">
                    <button slot="header-items" aria-label="Open profile menu" id="profileButton">
                        <span id="profileInitials"></span>
                    </button>
                </neptune-header-action-item>
            </nav>
            <button id="overflowButton" aria-label="Toggle navigation">
                <i class="fa-solid fa-ellipsis-vertical"></i>
            </button>
        </header>
    `;

    componentCssUrl = '/src/components/chrome/header/header.css';

    constructor() {
        super();
        this.headerActionItems = { apps: [], notifications: [], profile: [] };
        this.userInitials = '';
        this.sidenavMenuData = null;

        this.hamburgerButton = null;
        this.hamburgerClickHandler = null;
        this.overflowButton = null;
        this.overflowClickHandler = null;

        this.sidenavNavigateHandler = null;
    }

    async connectedCallback() {
        await super.connectedCallback();

        if (this.hasAttribute('header-action-items')) {
            const data = this.headerActionItems || window.headerActionItems;
            const hasData =
                data?.apps?.length > 0 ||
                data?.notifications?.length > 0 ||
                data?.profile?.length > 0;

            if (hasData) {
                this.headerActionItems = data;
                this.initializeHeader();
            } else {
                console.warn('neptune-header header-action-items flag set but no data found.');
            }
        } else {
            this.initializeHeader();
        }    }

    async disconnectedCallback() {
        await this.removeEventListeners();
        super.disconnectedCallback?.();
    }

    static get observedAttributes() {
        return ['header-action-items', 'user-initials'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) return;

        if (name === 'header-action-items') {
            this.updateHeaderActionItems();
        } else if (name === 'user-initials') {
            this.userInitials = newValue;
            this.updateProfileInitials();
        }
    }

    initializeHeader() {
        const attr = this.getAttribute('user-initials');
        if (attr) {
            this.userInitials = attr;
        }
        this.updateHeaderActionItems();
        this.setupMenuClosingLogic();
        this.setupSidenavListener();
        this.setEventListeners();
    }

    updateHeaderActionItems() {
        if (!this.isRendered) return;
        const nav = this.shadowRoot?.querySelector('#headerActionNav');
        if (!nav) return;

        const { apps = [], notifications = [], profile = [] } = this.headerActionItems;

        nav.setAttribute('data-has-notifications', notifications.length > 0 ? 'true' : 'false');
        nav.setAttribute('data-has-apps', apps.length > 0 ? 'true' : 'false');
        nav.setAttribute('data-has-profile', profile.length > 0 ? 'true' : 'false');

        const setItems = (selector, items) => {
            if (items.length > 0)
                nav.querySelector(selector)?.setAttribute('items', JSON.stringify(items));
        };

        setItems('#notificationActionItem', notifications);
        setItems('#appsActionItem', apps);
        setItems('#profileActionItem', profile);

        this.updateProfileInitials();
    }

    updateProfileInitials() {
        if (!this.isRendered) return;
        const profileInitials = this.shadowRoot?.querySelector('#profileInitials');
        if (profileInitials) {
            profileInitials.textContent = this.userInitials;
        }
    }

    closeAllMenus() {
        const actionItems = this.shadowRoot.querySelectorAll('neptune-header-action-item');
        actionItems.forEach(item => item.closeMenu?.());
    }

    setupMenuClosingLogic() {
        if (!this.isRendered) return;
        const actionItems = this.shadowRoot?.querySelectorAll('neptune-header-action-item');
        if (!actionItems) return;

        actionItems.forEach((item) => {
            item.addEventListener('menu-opened', (e) => {
                actionItems.forEach((otherItem) => {
                    if (otherItem !== item) {
                        otherItem.closeMenu();
                    }
                });
            });
            item.addEventListener('item-select', (e) => {
                this.dispatchEvent(new CustomEvent('header-item-selected', {
                    bubbles: true,
                    composed: true,
                    detail: { item: e.detail.item }
                }));
            });
        });
    }

    setupSidenavListener() {
        if (this.sidenavNavigateHandler) {
            const sidenav = document.querySelector('neptune-sidenav');
            if (sidenav) {
                sidenav.removeEventListener('neptune-sidenav-navigate', this.sidenavNavigateHandler);
            }
        }

        const sidenav = document.querySelector('neptune-sidenav');
        if (!sidenav) {
            return;
        }

        if (!this.sidenavMenuData && sidenav.menuData) {
            this.sidenavMenuData = JSON.parse(JSON.stringify(sidenav.menuData));
        }

        this.sidenavNavigateHandler = e => {
            const position = document.body.getAttribute('data-sidenav-position');

            if (position === 'right') {
                this.dispatchEvent(new CustomEvent('header-item-selected', {
                    bubbles: true,
                    composed: true,
                    detail: { item: { id: e.detail.id, name: e.detail.label, route: e.detail.route } }
                }));
            }
        };

        sidenav.addEventListener('neptune-sidenav-navigate', this.sidenavNavigateHandler);
    }

    async setEventListeners() {
        if (!this.isRendered) return;

        this.removeEventListeners();

        this.hamburgerButton = this.shadowRoot?.querySelector('#hamburgerButton');
        this.overflowButton = this.shadowRoot?.querySelector('#overflowButton');

        if (this.hamburgerButton) {
            this.hamburgerClickHandler = this.handleHamburgerClick.bind(this);
            this.hamburgerButton.addEventListener('click', this.hamburgerClickHandler);
        }

        if (this.overflowButton) {
            this.overflowClickHandler = this.handleOverflowClick.bind(this);
            this.overflowButton.addEventListener('click', this.overflowClickHandler);
        }
    }

    async removeEventListeners() {
        if (this.hamburgerButton && this.hamburgerClickHandler) {
            this.hamburgerButton.removeEventListener('click', this.hamburgerClickHandler);
        }
        this.hamburgerButton = null;
        this.hamburgerClickHandler = null;

        if (this.overflowButton && this.overflowClickHandler) {
            this.overflowButton.removeEventListener('click', this.overflowClickHandler);
        }
        this.overflowButton = null;
        this.overflowClickHandler = null;

        if (this.sidenavNavigateHandler) {
            const sidenav = document.querySelector('neptune-sidenav');
            if (sidenav) {
                sidenav.removeEventListener('neptune-sidenav-navigate', this.sidenavNavigateHandler);
            }
            this.sidenavNavigateHandler = null;
        }
    }

    handleHamburgerClick() {
        this.closeAllMenus();

        const sidenav = this.ensureSidenavExists();
        const position = sidenav.getAttribute('position') || 'left';
        const state = sidenav.getAttribute('state') || 'rail';

        if (position === 'right' && state === 'primary-drawer') {
            if (this.sidenavMenuData) {
                this.applySidenavMenuData(sidenav, this.sidenavMenuData);
            }
            sidenav.setAttribute('state', 'rail');
            sidenav.setAttribute('position', 'left');
            this.updateBodyAttributes({ leftOpen: false, rightOpen: false, navState: 'rail', position: 'left' });
        }

        const current = sidenav.getAttribute('state') || 'rail';
        const newState = current === 'rail' ? 'primary-drawer' : 'rail';

        sidenav.setAttribute('state', newState);
        sidenav.setAttribute('position', 'left');

        this.updateBodyAttributes({ leftOpen: newState !== 'rail', rightOpen: false, navState: newState, position: 'left' });
        this.emitHamburgerToggle({
            isOpen: newState !== 'rail',
            navState: newState,
            position: 'left'
        });
    }

    handleOverflowClick() {
        this.closeAllMenus();
        const sidenav = this.ensureSidenavExists();

        // Ensure listener is attached to the current sidenav
        this.setupSidenavListener();

        const position = sidenav.getAttribute('position') || 'left';
        const state = sidenav.getAttribute('state') || 'rail';

        const isRightOpen = position === 'right' && state === 'primary-drawer';

        if (isRightOpen) {
            sidenav.setAttribute('state', 'rail');
            sidenav.setAttribute('position', 'right');
            this.updateBodyAttributes({ leftOpen: false, rightOpen: false, navState: 'rail', position: 'right' });
            this.emitOverflowToggle({ isOpen: false, navState: 'rail', position: 'right' });
            return;
        }

        if (position === 'left' && state === 'primary-drawer') {
            sidenav.setAttribute('state', 'rail');
        }

        this.applySidenavMenuData(sidenav, this.buildHeaderMenuData());

        sidenav.setAttribute('state', 'primary-drawer');
        sidenav.setAttribute('position', 'right');

        this.updateBodyAttributes({ leftOpen: false, rightOpen: true, navState: 'primary-drawer', position: 'right' });
        this.emitOverflowToggle({ isOpen: true, navState: 'primary-drawer', position: 'right' });
    }

    buildHeaderMenuData() {
        const { apps = [], notifications = [], profile = [] } = this.headerActionItems;
        const attr = this.getAttribute('user-initials');
        const toChild = item => ({
            id: item.id,
            label: item.name || item.title || '',
            route: item.route
        });

        const sections = [];

        if (apps.length > 0)
            sections.push({ label: 'Apps', icon: 'fa-solid fa-grid', route: apps[0].route, subMenu: apps.map(toChild) });

        if (notifications.length > 0)
            sections.push({ label: 'Notifications', icon: 'fa-regular fa-bell', route: notifications[0].route, subMenu: notifications.map(toChild) });

        if (profile.length > 0)
            sections.push({ label: 'Profile', avatarText: attr, route: profile[0].route, subMenu: profile.map(toChild) });

        return sections;
    }

    applySidenavMenuData(sidenav, menuData) {
        const apply = () => {
            if (typeof sidenav.setMenuData === 'function') {
                sidenav.setMenuData(menuData);
            } else {
                sidenav.pendingMenuData = menuData;
            }
        };

        customElements.get('neptune-sidenav')
            ? apply()
            : customElements.whenDefined('neptune-sidenav').then(apply);
    }

    ensureSidenavExists() {
        let sidenav = document.querySelector('neptune-sidenav');
        if (!sidenav) {
            sidenav = document.createElement('neptune-sidenav');
            sidenav.setAttribute('state', 'rail');
            sidenav.setAttribute('position', 'left');
            document.body.appendChild(sidenav);
        }
        return sidenav;
    }

    updateBodyAttributes({ leftOpen, rightOpen, navState, position }) {
        document.body.setAttribute('data-left-nav-open', String(!!leftOpen));
        document.body.setAttribute('data-right-nav-open', String(!!rightOpen));
        document.body.setAttribute('data-sidenav-open', String(!!(leftOpen || rightOpen)));
        document.body.setAttribute('data-sidenav-state', navState);
        document.body.setAttribute('data-sidenav-position', position);
    }

    emitHamburgerToggle({ isOpen, navState, position }) {
        this.dispatchEvent(
            new CustomEvent('hamburger-toggle', {
                bubbles: true,
                composed: true,
                detail: { isOpen, navState, position }
            })
        );
    }

    emitOverflowToggle({ isOpen, navState, position }) {
        this.dispatchEvent(
            new CustomEvent('overflow-toggle', {
                bubbles: true,
                composed: true,
                detail: { isOpen, navState, position }
            })
        );
    }
}

customElements.define('neptune-header', Header);
export default Header;
